import { http } from '../lib/api/http';

const COLLECTION = 'feed_posts';

const normalizeSelectValue = value => {
  if (Array.isArray(value)) {
    return value.find(Boolean) || '';
  }
  if (value === null || value === undefined) return '';
  return value;
};

const normalizeCategoryList = value => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(entry => String(entry).trim()).filter(Boolean);
  }
  const text = String(value || '').trim();
  if (!text) return [];
  if ((text.startsWith('[') && text.endsWith(']')) || text.startsWith('{')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map(entry => String(entry).trim()).filter(Boolean);
      }
    } catch {
      // ignore parse errors and fallback to comma split
    }
  }
  return text
    .split(',')
    .map(entry => entry.trim())
    .filter(Boolean);
};

const isNewsCategory = value => {
  if (!value && value !== 0) return false;
  return String(value)
    .trim()
    .toLowerCase() === 'news';
};

const mapCompany = record => {
  if (!record) {
    return {
      id: '',
      name: '',
      slug: '',
      logoUrl: '',
    };
  }

  const companyRecord = Array.isArray(record) ? record[0] : record;
  const logoUrl =
    companyRecord?.logoUrl ||
    companyRecord?.logo_url ||
    '';
  const heroUrl =
    companyRecord?.heroUrl ||
    companyRecord?.hero_url ||
    '';
  const categories = normalizeCategoryList(
    companyRecord?.categories || companyRecord?.category
  );
  const primaryCategory =
    (typeof companyRecord?.category === 'string' && companyRecord.category.trim()) ||
    categories[0] ||
    '';
  const companyType =
    (typeof companyRecord?.companyType === 'string' && companyRecord.companyType.trim()) ||
    (typeof companyRecord?.type === 'string' && companyRecord.type.trim()) ||
    '';

  return {
    id: companyRecord?.id || '',
    name: companyRecord?.name || '',
    slug: companyRecord?.slug || '',
    logoUrl,
    heroUrl,
    heroOffset: typeof companyRecord?.heroOffset === 'number' ? companyRecord.heroOffset : 0,
    category: primaryCategory,
    categories,
    companyType,
    isNews:
      isNewsCategory(primaryCategory) ||
      categories.some(isNewsCategory) ||
      isNewsCategory(companyType),
  };
};

const mapUser = record => {
  if (!record) {
    return {
      id: '',
      name: '',
      email: '',
      username: '',
      avatarUrl: '',
    };
  }

  const userRecord = Array.isArray(record) ? record[0] : record;
  const avatarUrl = userRecord?.avatarUrl || userRecord?.avatar_url || '';
  return {
    id: userRecord?.id || '',
    name:
      userRecord?.name ||
      userRecord?.username ||
      userRecord?.email ||
      '',
    email: userRecord?.email || '',
    username: userRecord?.username || '',
    avatarUrl,
  };
};

const mapRequest = record => {
  if (!record) {
    return null;
  }

  const normalizedStatus = normalizeSelectValue(record.status);
  return {
    id: record.id,
    title: record.title || '',
    status: normalizedStatus || 'draft',
    pickupLocation: record.pickupLocation || '',
    dropoffLocation: record.dropoffLocation || '',
    neededBy: record.neededBy || '',
    budget: record.budget || '',
  };
};

const parseBooleanFlag = value => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    if (['true', '1', 'yes', 'y', 'si', 'sí'].includes(normalized)) return true;
  }
  return false;
};

const mapRecord = record => {
  if (!record) return null;

  const companySource = record.company || record.expand?.company;
  const authorSource = record.author || record.expand?.createdBy;
  const requestSource = record.attached_request || record.expand?.attachedRequest;

  const company = mapCompany(companySource);
  const author = mapUser(authorSource);
  const attachedRequest = mapRequest(requestSource);
  const normalizedCompanyCategories = Array.isArray(company?.categories)
    ? company.categories
    : [];
  const companyCategory = normalizeSelectValue(record.companyCategory) || company?.category || '';
  const companyType = normalizeSelectValue(record.companyType) || company?.companyType || '';
  const recordCategory = normalizeSelectValue(record.category);
  const recordType = normalizeSelectValue(record.type);
  const recordPostType = normalizeSelectValue(record.postType);
  const extraCategories = Array.isArray(record.companyCategories)
    ? record.companyCategories
    : normalizeCategoryList(record.companyCategories);
  const tagCategories = normalizeCategoryList(record.tags);
  const allCategories = [
    companyCategory,
    companyType,
    company?.category,
    company?.companyType,
    recordCategory,
    recordType,
    recordPostType,
    ...normalizedCompanyCategories,
    ...extraCategories,
    ...tagCategories,
  ].filter(Boolean);
  const visibility = normalizeSelectValue(record.visibility) || 'network';
  const recordIsNews =
    parseBooleanFlag(record.isNews) ||
    isNewsCategory(recordPostType) ||
    isNewsCategory(recordType) ||
    isNewsCategory(recordCategory) ||
    visibility === 'news';
  const isNews = recordIsNews || company?.isNews || allCategories.some(isNewsCategory);
  const newsLink =
    record.newsLink ||
    record.linkUrl ||
    record.link ||
    record.url ||
    '';

  return {
    id: record.id,
    content: record.content || '',
    visibility,
    status: normalizeSelectValue(record.status) || 'published',
    created: record.created_at || record.created || '',
    updated: record.updated_at || record.updated || '',
    companyId: record.company_id || record.companyId || record.company || company.id,
    createdBy: record.created_by || record.createdBy || author.id,
    company,
    author,
    attachedRequestId:
      record.attached_request_id ||
      record.attachedRequest ||
      attachedRequest?.id ||
      '',
    attachedRequest,
    companyCategory,
    companyCategories: [...new Set(allCategories.map(value => String(value).trim()))],
    companyType,
    isNews,
    newsLink: typeof newsLink === 'string' ? newsLink.trim() : '',
  };
};

const buildPayload = payload => {
  const data = {
    content: payload.content?.trim() || '',
    visibility: payload.visibility || 'network',
    status: payload.status || 'published',
  };

  if (
    Object.prototype.hasOwnProperty.call(payload, 'company') ||
    Object.prototype.hasOwnProperty.call(payload, 'companyId')
  ) {
    const companyValue =
      payload.company !== undefined ? payload.company : payload.companyId;
    data.company_id = companyValue || null;
  }

  if (payload.createdBy || payload.creatorId) {
    data.created_by = payload.createdBy || payload.creatorId;
  }

  if (
    Object.prototype.hasOwnProperty.call(payload, 'attachedRequest') ||
    Object.prototype.hasOwnProperty.call(payload, 'attachedRequestId')
  ) {
    const requestValue =
      payload.attachedRequest !== undefined
        ? payload.attachedRequest
        : payload.attachedRequestId;
    data.attached_request_id = requestValue || null;
  }

  if (
    Object.prototype.hasOwnProperty.call(payload, 'newsLink') ||
    Object.prototype.hasOwnProperty.call(payload, 'linkUrl') ||
    Object.prototype.hasOwnProperty.call(payload, 'link')
  ) {
    const link =
      payload.newsLink ||
      payload.linkUrl ||
      payload.link;
    data.newsLink = link ? String(link).trim() : '';
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'isNews')) {
    data.isNews = Boolean(payload.isNews);
  }

  return data;
};

export const FEED_POST_VISIBILITY_OPTIONS = [
  { value: 'network', label: 'Red Flotte' },
  { value: 'followers', label: 'Seguidores' },
  { value: 'private', label: 'Privado' },
];

export const FEED_POST_STATUS_OPTIONS = [
  { value: 'draft', label: 'Borrador' },
  { value: 'published', label: 'Publicado' },
  { value: 'archived', label: 'Archivado' },
];

export async function listFeedPosts({
  limit = 20,
  page = 1,
  companyId,
  createdBy,
  withMeta = false,
} = {}) {
  const params = {
    page,
    per_page: limit,
  };
  if (companyId) {
    params.company_id = companyId;
  }
  if (createdBy) {
    params.created_by = createdBy;
  }
  if (withMeta) {
    params.with_meta = true;
  }

  const response = await http.get(`/${COLLECTION}`, { params });
  const payload = response.data;
  if (withMeta && payload && Array.isArray(payload.items)) {
    const items = payload.items.map(mapRecord);
    const totalPages = Number(payload.total_pages || payload.totalPages || 0);
    const currentPage = Number(payload.page || page || 1);
    const totalItems = Number(payload.total || payload.totalItems || items.length || 0);
    const hasMore = totalPages ? currentPage < totalPages : items.length === limit;
    return {
      items,
      page: currentPage,
      totalPages,
      totalItems,
      hasMore,
    };
  }

  const rawItems = Array.isArray(payload) ? payload : payload?.items;
  const items = Array.isArray(rawItems) ? rawItems.map(mapRecord) : [];
  return items;
}

export async function createFeedPost(payload) {
  const data = buildPayload(payload);
  const response = await http.post(`/${COLLECTION}`, data);
  return mapRecord(response.data);
}

export async function updateFeedPost(id, payload) {
  if (!id) {
    throw new Error('El identificador de la publicacion es requerido.');
  }
  const data = buildPayload(payload);
  const response = await http.patch(`/${COLLECTION}/${encodeURIComponent(id)}`, data);
  return mapRecord(response.data);
}

export async function deleteFeedPost(id) {
  if (!id) return false;
  await http.delete(`/${COLLECTION}/${encodeURIComponent(id)}`);
  return true;
}
